import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef }  from "react";
import classNames from "classnames";
import { TextBox } from "./text-box/text-box";
import { LaraGlobalContext } from "../lara-global-context";
import { ManagedInteractive, ManagedInteractiveImperativeAPI } from "./managed-interactive/managed-interactive";
import { ActivityLayouts, displayedQuestionNumber, isNotVisibleEmbeddable } from "../../utilities/activity-utils";
import { EmbeddablePlugin } from "./plugins/embeddable-plugin";
import { initializePlugin, IPartialEmbeddablePluginContext, validateEmbeddablePluginContextForWrappedEmbeddable
        } from "../../utilities/plugin-utils";
import { EmbeddableType, IEmbeddablePlugin } from "../../types";
import { IInteractiveSupportedFeaturesEvent } from "../../lara-plugin/events";
import { ICustomMessage, ISupportedFeatures, INavigationOptions, IGetInteractiveState } from "@concord-consortium/lara-interactive-api";
import { SpikeMediaLibrary } from "./spike-media-library/spike-media-library";
import { EmbeddableVisibilityContext } from "../embeddable-visibility-context";
import { useQuestionGateReporter, useQuestionLock } from "./disabled-questions-context";
import { DisabledQuestionsBanner } from "./disabled-questions-banner";
import { useInert } from "../../utilities/use-inert";

import "./embeddable.scss";

interface IProps {
  embeddable: EmbeddableType;
  linkedPluginEmbeddable?: IEmbeddablePlugin;
  sectionLayout: string;
  displayMode?: string;
  activityLayout?: number;
  questionNumber?: number;
  teacherEditionMode?: boolean;
  setNavigation?: (id: string, options: INavigationOptions) => void;
  pluginsLoaded: boolean;
  ref?: React.Ref<EmbeddableImperativeAPI>;
  hideQuestionNumbers?: boolean;
  saveInteractiveStateHistory?: boolean;
  addRefToQuestionMap?: (refId: string, ref: any) => void;
}

export interface EmbeddableImperativeAPI {
  saveInteractiveStateBeforeClose: (options?: IGetInteractiveState) => Promise<void>;
}

type ISendCustomMessage = (message: ICustomMessage) => void;

export const Embeddable: React.ForwardRefExoticComponent<IProps> = forwardRef((props, ref) => {
  const { embeddable, sectionLayout, activityLayout, linkedPluginEmbeddable, displayMode, questionNumber, setNavigation,
    teacherEditionMode, pluginsLoaded, hideQuestionNumbers, saveInteractiveStateHistory } = props;
  const handleSetNavigation = useCallback((options: INavigationOptions) => {
    setNavigation?.(embeddable.ref_id, options);
  }, [setNavigation, embeddable.ref_id]);
  const managedInteractiveRef = useRef<ManagedInteractiveImperativeAPI>(null);
  const embeddableWrapperDivTarget = useRef<HTMLInputElement>(null);
  const embeddableDivTarget = useRef<HTMLInputElement>(null);
  const targetDiv = useRef<HTMLDivElement>(null);
  const sendCustomMessageRef = useRef<ISendCustomMessage | undefined>(undefined);
  const setSendCustomMessage = useCallback((sender: ISendCustomMessage) => {
    sendCustomMessageRef.current = sender;
  }, []);
  const LARA = useContext(LaraGlobalContext);
  const lock = useQuestionLock(embeddable.ref_id);
  const reportGateEvent = useQuestionGateReporter(embeddable.ref_id);
  useInert(embeddableWrapperDivTarget, lock.disabled);

  useEffect(() => {
    const sendCustomMessage = (message: ICustomMessage) => sendCustomMessageRef.current?.(message);
    const pluginContext: IPartialEmbeddablePluginContext = {
      LARA,
      embeddable: linkedPluginEmbeddable,
      embeddableContainer: embeddableWrapperDivTarget.current || undefined,
      wrappedEmbeddable: embeddable,
      wrappedEmbeddableContainer: embeddableDivTarget.current || undefined,
      sendCustomMessage
    };
    const validPluginContext = validateEmbeddablePluginContextForWrappedEmbeddable(pluginContext);
    if (validPluginContext && pluginsLoaded) {
      initializePlugin(validPluginContext);
    }
  }, [LARA, linkedPluginEmbeddable, embeddable, pluginsLoaded]);

  const visibility = useContext(EmbeddableVisibilityContext);
  useEffect(() => {
    const element = targetDiv.current;
    if (!visibility || !element || embeddable.is_hidden) return;
    const shownQuestionNumber = displayedQuestionNumber(questionNumber, hideQuestionNumbers);
    return visibility.register(element, {
      embeddableId: embeddable.ref_id,
      embeddableTitle: embeddable.name?.trim() ?? "",
      ...(shownQuestionNumber ? { questionNumber: shownQuestionNumber } : {})
    });
  }, [visibility, embeddable.ref_id, embeddable.name, embeddable.is_hidden, questionNumber, hideQuestionNumbers]);

  useImperativeHandle(ref, () => ({
    saveInteractiveStateBeforeClose: (options?: IGetInteractiveState) => managedInteractiveRef.current?.saveInteractiveStateBeforeClose(options) || Promise.resolve()
  }));

  const handleSetSupportedFeatures = useCallback((container: HTMLElement, features: ISupportedFeatures) => {
    const event: IInteractiveSupportedFeaturesEvent = {
      container,
      supportedFeatures: features
    };
    LARA?.Events.emitInteractiveSupportedFeatures(event);
  }, [LARA?.Events]);

  // used by ManagedInteractive when click to play is enabled to signal the interactive
  // is available after click to play is clicked
  const handleEmitInteractiveAvailable = useCallback(() => {
    const container = embeddableWrapperDivTarget.current;
    if (container) {
      LARA?.Events.emitInteractiveAvailable({ container, available: true });
    }
  }, [LARA?.Events]);

  const showQuestionPrefix = activityLayout !== ActivityLayouts.Notebook;

  let qComponent;
  if (embeddable.type === "MwInteractive" || (embeddable.type === "ManagedInteractive" && embeddable.library_interactive)) {
    qComponent = <ManagedInteractive
                    ref={managedInteractiveRef}
                    embeddable={embeddable}
                    questionNumber={questionNumber}
                    setSupportedFeatures={handleSetSupportedFeatures}
                    setSendCustomMessage={setSendCustomMessage}
                    setNavigation={handleSetNavigation}
                    emitInteractiveAvailable={handleEmitInteractiveAvailable}
                    showQuestionPrefix={showQuestionPrefix}
                    hideQuestionNumbers={hideQuestionNumbers}
                    saveInteractiveStateHistory={saveInteractiveStateHistory}
                    disabled={lock.disabled}
                    locked={lock.locked}
                    onQuestionGateEvent={reportGateEvent}
                 />;
  } else if (embeddable.type === "ManagedInteractive" && !embeddable.library_interactive) {
    qComponent = <div>Content type not supported</div>;
  } else if (embeddable.type === "Embeddable::EmbeddablePlugin" && embeddable.plugin?.component_label === "windowShade") {
    qComponent = teacherEditionMode ? <EmbeddablePlugin embeddable={embeddable} pluginsLoaded={pluginsLoaded} /> : undefined;
  } else if (embeddable.type === "Embeddable::Xhtml") {
    qComponent = <TextBox embeddable={embeddable} />;
  } else if (embeddable.type === "Embeddable::SpikeMediaLibrary") {
    qComponent = <SpikeMediaLibrary embeddable={embeddable} />;
  } else if (isNotVisibleEmbeddable(embeddable)) {
    qComponent = undefined;
  } else {
    qComponent = <div>Content type not supported</div>;
  }

  // The following conditional prevents teacher edition containers from being rendered
  // when not in teacher edition mode. LARA handles this differently by using a mutation observer
  // (see https://github.com/concord-consortium/lara/blob/master/app/views/plugins/_show.haml).
  // It would be better to do that here as well if we update Activity Player to not use direct
  // dependencies on teacherEditionMode.
  if (qComponent === undefined) {
    return null;
  }

  const singlePageLayout = activityLayout === ActivityLayouts.SinglePage;
  const isFullWidthLayout = sectionLayout === "full-width" || singlePageLayout;

  let hasMaxAspectRatio = false;

  if (embeddable.type === "ManagedInteractive" && !embeddable.inherit_aspect_ratio_method) {
    hasMaxAspectRatio = embeddable.custom_aspect_ratio_method === "MAX";
  } else if (embeddable.type === "ManagedInteractive" && embeddable.inherit_aspect_ratio_method) {
    hasMaxAspectRatio = embeddable.library_interactive?.data.aspect_ratio_method === "MAX";
  } else {
    hasMaxAspectRatio = embeddable.aspect_ratio_method === "MAX";
  }

  const embeddableClasses = classNames("embeddable",
                                        isFullWidthLayout
                                          ? "full-width"
                                          : embeddable.column === "primary"
                                            ? "primary"
                                            : displayMode === "stacked"
                                              ? "secondary stacked"
                                              : "secondary",
                                        {"half-width":  embeddable.is_half_width && !singlePageLayout},
                                        {"hidden": embeddable.is_hidden},
                                        {"max-aspect-ratio": isFullWidthLayout && hasMaxAspectRatio},
                                        {"disabled-question": lock.locked}
                                      );

  return (
    <div
      className={embeddableClasses}
      data-cy="embeddable"
      key={embeddable.ref_id}
      ref={targetDiv}
    >
      { lock.banner && <DisabledQuestionsBanner banner={lock.banner} /> }
      { linkedPluginEmbeddable && <div className={"embeddable-sub-one"} ref={embeddableWrapperDivTarget}></div> }
      <div className={"embeddable-sub-two"} ref={embeddableDivTarget}>
        { qComponent }
      </div>
    </div>
  );
});
Embeddable.displayName = "Embeddable";
